import { completeOnboardingProfile, setupOnboardingOrganization } from "features/onboarding/services/organizationSetup";
import { useSubscriptionDataLayers } from "./hooks/useSubscriptionDataLayers";
import { getWMSLayerCountries } from "./utils/getWMSLayerCountries";
import React, { useCallback, useRef, ErrorInfo, createContext, useEffect, useReducer, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
// third-party
import { TCountryCode } from "countries-list";
import { httpsCallable, type HttpsCallableResult } from "firebase/functions";
import { audienceContent as AudienceContentTypes, firestore as FirestoreTypes } from "@foresting-tomorrow/api_types_firestore";
import { getLogoutPath } from "utils/audienceSafety";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  signInAnonymously,
  createUserWithEmailAndPassword,
  User,
  GoogleAuthProvider,
  TwitterAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
} from "firebase/auth";

// import { getAnalytics } from "firebase/analytics";
import {
  collection,
  addDoc,
  onSnapshot,
  doc,
  query,
  where,
  orderBy,
  setDoc,
  updateDoc,
  getDoc,
  Unsubscribe,
  getDocs,
  deleteDoc,
} from "firebase/firestore";

import dayjs from "dayjs";
import axios from "axios";
import { Crisp } from "crisp-sdk-web";

// Mixpanel
import mixpanel from "mixpanel-browser";

// action - state management
import {
  LOGIN,
  LOGOUT,
  IS_INITIALIZED,
  CHILDREN_IS_INITIALIZED,
  SET_CURRENT_SOFTWARE_VERSION,
  SET_COUNTRY_CODES,
  SET_URL_PARAMS,
  FETCH_USER_ORG_SUCCESS,
  FETCH_ORG_PRIVATE_DATA_SUCCESS,
  FETCH_ORG_TO_ORG_PROVIDER_SUBSCRIPTIONS_SUCCESS,
  FETCH_ORG_TO_ORG_CONSUMER_SUBSCRIPTIONS_SUCCESS,
  FETCH_USER_ONBOARDING_OBJECT_SUCCESS,
  SET_ERROR,
  SET_LOADING,
  DELETE_TEAM_MEMBER_FROM_STATE,
  ADDED_ORG_CONTACT_SUCCESS,
  MODIFIED_ORG_CONTACT_SUCCESS,
  REMOVED_ORG_CONTACT_SUCCESS,
  ORG_CONTACTS_EMPTY,
  SET_BUYER_ORG_DATA,
  DELETE_CONTACT_FROM_STATE,
  ADDED_ORG_TEAM_MEMBER_SUCCESS,
  MODIFIED_ORG_TEAM_MEMBER_SUCCESS,
  REMOVED_ORG_TEAM_MEMBER_SUCCESS,
  FETCH_SPECIES_DATA_SUCCESS,
  FETCH_ACTIVITY_DATA_SUCCESS,
  FETCH_SUBSCRIPTION_ADD_ONS_SUCCESS,
  FETCH_EXTERNAL_WMS_LAYERS_SUCCESS,
  FETCH_EXTERNAL_ORG_DATA_LAYERS_SUCCESS,
  FETCH_EXTERNAL_REST_LAYERS_SUCCESS,
  ADDED_SPECIES_SCHEME,
  MODIFIED_SPECIES_SCHEME,
  REMOVED_SPECIES_SCHEME,
  SET_SELECTED_SPECIES_SCHEME_ID,
  GOOGLE_PLACES_AUTOCOMPLETE_LOADED,
  SET_BACKGROUND_PRINT_JOB_LOADING,
  FETCH_PROVIDER_ORG_TO_ORG_SUBSCRIPTION_TYPES_SUCCESS,
  SET_CURRENT_AI_ASSISTANT_MESSAGES,
  SET_CURRENT_AI_ASSISTANT_THREAD_ID,
  SET_OPEN_AI_CHAT_WINDOW,
} from "store/reducers/actions";
import authReducer from "store/reducers/auth";

// project import
import { firebaseApp, firestore, auth, functions } from "lib/firebase/firebase";
import Loader from "components/Loader";
import {
  DEFAULT_TRIAL_SUBSCRIPTION,
  LOADING_TIMER,
  conversionSignupUrl,
  conversionSubscriptionUrl,
} from "config/config-general";
import config from "config/config";
import { convertUrlQueryString } from "features/map/utils/convertUrlQueryString";
import {
  AiChatMessage,
  AudienceMapInformation,
  AuthProps,
  CloudFunctionNames,
  FirebaseContextType,
  OnboardingObject,
  OrgToOrgSubscription,
  RoleTypes,
  SubscriptionAddOn,
  SubscriptionTypes,
  UpdateUserProfileProps,
  UserOrg,
  UserSettingsObject,
} from "types/auth";
import { useTranslation } from "utils/locales/utilityFunctions";
import anonymousConfig from "config/appConfigFiles/anonymous.json";
import useAppFunctionalityConfig from "hooks/useAppFunctionalityConfig";
import { AppConfigOverride } from "types/appFunctionality";
import { getAppConfigFromSubscription } from "config/utils/getAppConfig";
import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import { loadGooglePlacesAutocompleteScript } from "utils/scriptLoader";
import { dataObjectWithoutUndefinedFields } from "utils/dataManipulation";
import useAppSettings from "hooks/useAppSettings";
import {
  mapOnboardingStepToMixpanelUserOnbardingStep,
  mixpanelEvents,
} from "config/servicesConfig/mixpanelEvents";
import { chunk } from "lodash";
import { trackLinkedInVirtualPageView } from "services/linkedIn/linkedIn";
import { forslerPartnerId } from "config/servicesConfig/linkedIn";
import { forslerPixelId } from "config/servicesConfig/facebook";
import { trackFacebookEventWithUrl } from "services/meta/facebook";
import useNetworkStatus from "hooks/useNetworkStatus";
import { BrandingAssetKey, UpdateBrandingPackageType } from "types/branding";
import { useOrgBranding } from "hooks/useBrandingFromOrg";
import { getInternalAppType } from "utils/brandingUtils";
import useConfig from "hooks/useConfig";
import { passwordReset } from "@foresting-tomorrow/api_types_routes";

// --- Mixpanel initialize --- //
mixpanel.init(config.MP_TOKEN, {
  debug: config.MP_DEBUG,
  api_host: "https://api-eu.mixpanel.com",
  // track_pageview: "full-url",
  persistence: "localStorage",
});
(window as any).mixpanel = mixpanel;

// Get callable http functions from backend
// Init functions
const initUserAndOrg = httpsCallable(functions, "init-userAndOrg");
// Tools functions
const getNextAvailableId = httpsCallable(functions, "tools-getNextAvailableId");
const checkCustomIdAvailable = httpsCallable(functions, "tools-checkCustomIdAvailable");
const releaseReservedId = httpsCallable(functions, "tools-releaseReservedId");
const reserveNextId = httpsCallable(functions, "tools-reserveNextId");
const reserveCustomId = httpsCallable(functions, "tools-reserveCustomId");
// Team functions TODO: Move to _v2 function - need to be in prod also
const createTeamMember = httpsCallable(functions, "team-create");
const readTeamMember = httpsCallable(functions, "team-read");
const updateTeamMember = httpsCallable(functions, "team-update");
const deleteTeamMember = httpsCallable(functions, "team-delete");
// Contact functions
const createContact = httpsCallable(functions, "contacts-create");
const readContact = httpsCallable(functions, "contacts-read");
const updateContact = httpsCallable(functions, "contacts-update");
const deleteContact = httpsCallable(functions, "contacts-delete");
// Data functions
const deleteOrgAccount = httpsCallable(functions, "data-deleteOrgAccount");
const deleteUserAccount = httpsCallable(functions, "data-deleteUserAccount");
const setDataStatus = httpsCallable(functions, "data-setDataStatus");
const updateUsersBatch = httpsCallable(functions, "data-updateUsersBatch");
const createMetaData = httpsCallable(functions, "data-createMetaElement");
const updateMapData = httpsCallable(functions, "data-updateMapData");
const getOrgData = httpsCallable(functions, "data-getOrgData");
const orgToOrgSubscription = httpsCallable(functions, "data-orgToOrgSubscription");
const syncSubscription = httpsCallable(functions, "data-syncReepaySubscription");
const updateSubscription = httpsCallable(functions, "data-updateReepaySubscription");
const getCheckoutSession = httpsCallable(functions, "data-getReepayCheckoutSession");
const reportLayerError = httpsCallable(functions, "data-reportExternalDataLayerError");
// Branding functions
const ensureBrandingPackage = httpsCallable(functions, "data-ensureBrandingPackage");
const getBrandingUploadUrl = httpsCallable(functions, "data-getBrandingUploadUrl");
const commitBrandingAsset = httpsCallable(functions, "data-commitBrandingAsset");
const removeBrandingAsset = httpsCallable(functions, "data-removeBrandingAsset");
const updateBrandingPackageFields = httpsCallable(functions, "data-updateBrandingPackageFields");
// Audience functions
const setAudienceState = httpsCallable(functions, "audience-set");
const createAudienceContentAssetUploadUrl = httpsCallable(functions, "audienceContent-createAssetUploadUrl");
const commitAudienceContentAsset = httpsCallable(functions, "audienceContent-commitAsset");
const saveAudienceContentRevision = httpsCallable(functions, "audienceContent-saveRevision");
const deleteAudienceContent = httpsCallable(functions, "audienceContent-deleteContent");
const saveAudienceContentTemplateRevision = httpsCallable(functions, "audienceContent-saveTemplateRevision");
const listAudienceContentTemplates = httpsCallable(functions, "audienceContent-listTemplates");
const applyAudienceContentTemplate = httpsCallable(functions, "audienceContent-applyTemplate");
const deleteAudienceContentTemplate = httpsCallable(functions, "audienceContent-deleteTemplate");
// External services
const getCompanyData = httpsCallable(functions, "externalServices-getCompanyData");
const trackEvent = httpsCallable(functions, "externalServices-trackEvent");
const requestPasswordReset = httpsCallable<
  passwordReset.RequestBody,
  passwordReset.RequestResponse
>(functions, passwordReset.request.callableName);

const getter = <A,>(url: string) => {
  let data: null | A = null;
  let authed = false;
  return async () => {
    if (authed) return data;
    try {
      const token = await getAuth(firebaseApp)?.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const res = await axios.get<A>(url, { headers: { Authorization: "Bearer " + token } });
      data = res.data;
      authed = true;
      return data;
    } catch (error) {
      console.error("Error while getting a key", error);
      return null;
    }
  };
};

export const getOpenWeather = getter<{ apiKey: string }>(
  config.CLOUD_FUNCTIONS_URL + "keyGetters-getOpenWeather",
);
export const getTodaysforestInternalApiKey = getter<string>(
  config.CLOUD_FUNCTIONS_URL + "keyGetters-getTodaysforestInternalApiKey",
);
export const getGoogleMapsApiKey = getter<string>(
  config.CLOUD_FUNCTIONS_URL + "keyGetters-getGoogleMapsApiKey",
);

// Firestore event listeners
let firestoreEventListeners: any[] = [];
let orgContactsUnsubscribes: Unsubscribe[] = [];
let orgInvitesUnsubscribes: Unsubscribe[] = [];

// const
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  googlePlacesAutocompleteLoaded: false,
  childrenInitialized: false,
  currentSoftwareVersion: "",
  countryCodes: undefined,
  error: null,
  user: null,
  userOnboardingObject: null,
  userOrg: null,
  providerOrgToOrgSubscriptionTypes: null,
  orgToOrgProviderSubscriptions: null,
  orgToOrgConsumerSubscriptions: null,
  urlParams: null,
  loading: false,
  orgTeam: {},
  orgContacts: {},
  buyerOrgsById: {},
  cloudFunctionLoading: false,
  cloudFunctionError: null,
  removeUserId: null,
  species: {},
  activityTypes: {},
  subscriptionAddOns: null,
  speciesSchemes: null,
  selectedSpeciesSchemeId: null,
  externalWMSLayers: null,
  externalOrgDataLayers: null,
  externalRESTLayers: null,
  backgroundPrintJobLoading: false,
  aiAssistant: {
    currentThreadId: null,
    currentMessages: [],
    openAiChatWindow: false,
  },
};

// ==============================|| FIREBASE CONTEXT & PROVIDER ||============================== //
const FirebaseContext = createContext<FirebaseContextType | null>(null);

export const FirebaseProvider = ({ children }: { children: React.ReactElement }) => {
  // --- Hooks --- //
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { i18n } = useConfig();
  const {
    trialSubscription,
    appSubConfigInitialUserSettingsState,
    fetchUserAppSettings,
    fetchOrgAppSettings,
    updateUserAppSettings,
    setTrialSubscription,
    setAppSubConfigInitialUserSettingsState,
    setFullState,
    setMapBackgroundLayer,
  } = useAppSettings();
  const [state, dispatch] = useReducer(authReducer, initialState);
  const buyerOrgFetchesRef = useRef<Set<string>>(new Set());
  const {
    onChangeAppFunctionalityConfig,
    resetAppFunctionalityConfig,
    mapPagePathToConfigName,
    config: appConfig,
    isDomainReadOnly,
    setAnonymousSession,
    assertDomainMutationAllowed,
  } = useAppFunctionalityConfig();
  const { isOnline } = useNetworkStatus();
  useOrgBranding(state.userOrg, state.user);

  const internalAppType = getInternalAppType({ pathOrHost: window.location.hostname });
  const trialConfigOrgIdRef = useRef<string | null>(null);
  // Clear profile and resource-role overlays when changing account or organisation.
  useEffect(() => {
    resetAppFunctionalityConfig();
    trialConfigOrgIdRef.current = null;
    if (state.user?.authType === "anonymous") {
      onChangeAppFunctionalityConfig(anonymousConfig as AppConfigOverride, "anonymous");
    }
  }, [state.user?.id, state.user?.authType, state.userOrg?.id, resetAppFunctionalityConfig, onChangeAppFunctionalityConfig]);

  // --- state and refs --- //
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  const [userProfileLoadedFromServer, setUserProfileLoadedFromServer] = useState(false);
  const subscriptionSyncedRef = useRef(false);
  const orgPrivateDataFetchedRef = useRef(false);
  const orgAdditionDataFetchedRef = useRef(false);
  const someConversionSentRef = useRef(false);
  const orgExternalLayersFetchedRef = useRef(false);
  const tempTrialSubscriptionRef = useRef<SubscriptionTypes | "unknown">("unknown");
  const tempUserRoleRef = useRef<"admin" | "not-admin">("not-admin");
  const mpDistinctIdRef = useRef<string | null>(null);
  const initWithoutOrgRef = useRef<string | null>(null);

  const location = useLocation();
  // --- Auth state change effect hook --- //
  useEffect(
    () => {
      const unsubscribeAuth = onAuthStateChanged(auth, (user: any) => {
        setAnonymousSession(Boolean(user?.isAnonymous));
        if (user) {
          setLoadingUserInfo(true);
          setUserProfileLoadedFromServer(false);
          // Fetch from firestore
          fetchCurrentSoftwareVersion();
          fetchUserData(user);
          fetchSpeciesList("extended");
          fetchActivityTypes("extended");
          if (!isDomainReadOnly()) fetchSubscriptionAddOns();
          fetchUserAppSettings(user.uid);
          // Setup third party services
          Crisp.configure(config.CRISP_KEY);
          Crisp.chat.hide(); // <-- enabled after life ring hide
          mixpanel.identify(user.uid);
          // Merge distinct_id and user_id for Mixpanel
          if (mpDistinctIdRef.current) {
            callCloudFunction("trackEvent", {
              event: mixpanelEvents.mergeDistinctIdAndUserId,
              data: {
                $device_id: mpDistinctIdRef.current,
                $user_id: user.uid,
              },
            });
          }
          // Sent events to SoMe accounts
          const url = `${window.location.host}/${conversionSignupUrl}`;
          trackLinkedInVirtualPageView(forslerPartnerId, url);
          trackFacebookEventWithUrl(forslerPixelId, "PageView", url);
          // Load global scripts
          loadGlobalScripts();
        } else {
          // Unsubscribe from event listeners
          // if (!state.isLoggedIn && firestoreEventListeners.length > 0) {
          //   firestoreEventListeners.forEach((el) => el());
          // }
          // if (orgContactsUnsubscribe) orgContactsUnsubscribe();
          // if (orgInvitesUnsubscribe) orgInvitesUnsubscribe();
          unsubscribeAllFirestoreListeners();
          resetAuthRefs();
          setLoadingUserInfo(true);
          setUserProfileLoadedFromServer(false);
          // Reset mixpanel user
          mixpanel.reset();
          dispatch({
            type: LOGOUT,
          });
        }
      });
      return () => {
        unsubscribeAuth();
        unsubscribeAllFirestoreListeners();
        resetAuthRefs();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  useEffect(() => {
    if (!state.isInitialized || !state.isLoggedIn || !initWithoutOrgRef.current || !loadingUserInfo)
      return;
    if (state.user?.authType !== "anonymous" && !userProfileLoadedFromServer) return;
    // Password-reset pages must be usable even if this browser has an active session.
    // They are handled by the guest route rather than the authenticated application layout.
    if (["/forgot-password", "/check-mail", "/reset-password"].includes(location.pathname)) {
      setLoadingUserInfo(false);
      return;
    }
    // Initialize app based on user data and subscription config data
    if (initWithoutOrgRef.current === "yes") {
      setTimeout(() => {
        setLoadingUserInfo(false);
      }, LOADING_TIMER);
      return;
    }
    // Get current path
    const currentPath = location.pathname;
    const configPathName = mapPagePathToConfigName(currentPath);
    // Check if path is enabled in config
    const { layout, defaultPath } = appConfig;
    if (configPathName === "unknown") {
      navigate(defaultPath, { replace: true });
    }
    // @ts-expect-error
    if (layout[configPathName] !== "enabled") {
      navigate(defaultPath, { replace: true });
    }
    setTimeout(() => {
      setLoadingUserInfo(false);
    }, LOADING_TIMER);
  }, [
    state.isInitialized,
    state.isLoggedIn,
    state.user?.authType,
    initWithoutOrgRef.current,
    appConfig,
    userProfileLoadedFromServer,
    location.pathname,
  ]);

  function initializeMapsApp() {
    dispatch({
      type: GOOGLE_PLACES_AUTOCOMPLETE_LOADED,
    });
  }

  async function loadGlobalScripts() {
    try {
      const googleMapsApiKey = await getGoogleMapsApiKey();
      if (googleMapsApiKey) {
        loadGooglePlacesAutocompleteScript(googleMapsApiKey, initializeMapsApp);
      }
    } catch (error) {
      console.error("Error loading google places autocomplete", error);
    }
  }

  // --- Set user app settings data --- //
  useEffect(() => {
    if (state.user?.authType !== "anonymous" && !userProfileLoadedFromServer) return;
    if (state.user?.authType === "anonymous") {
      // Set anonymous app config
      setMapBackgroundLayer("googleTerrain");
      return;
    }
    // Check when user has not yet created an org (only in the beginning of creating the user and onboarding)
    if (
      !state.userOrg ||
      Object.keys(state.userOrg).length === 0 ||
      !state.orgPrivateData ||
      Object.keys(state.orgPrivateData).length === 0
    ) {
      // Check if user has been onboarded
      if (state.user && state.user.id && state.user.onboarded === false) {
        // Initialize subscription
        updateUserAppSettings(state.user.id!, { trialSubscription: DEFAULT_TRIAL_SUBSCRIPTION });
        setTrialSubscription(DEFAULT_TRIAL_SUBSCRIPTION);
        const newConfig = getAppConfigFromSubscription(
          { type: DEFAULT_TRIAL_SUBSCRIPTION },
          "admin",
          internalAppType,
        );
        onChangeAppFunctionalityConfig(newConfig, DEFAULT_TRIAL_SUBSCRIPTION);
        // Initialize user background layer
        setMapBackgroundLayer("googleTerrain");
        return;
      }
    }
    // Check when user has an org
    if (state.user && state.user.id && state.userOrg && state.orgPrivateData) {
      // Check for trial subscription
      if (state.userOrg.subscription?.type === "trial") {
        let userRole: "admin" | "not-admin" = "not-admin";
        if (state.orgPrivateData.roles && state.orgPrivateData.roles[state.user.id]) {
          userRole = state.orgPrivateData.roles[state.user.id].role;
          if (userRole === "not-admin") {
            const newConfig = getAppConfigFromSubscription({ type: "basic" }, userRole, internalAppType);
            onChangeAppFunctionalityConfig(newConfig, "basic");
            tempTrialSubscriptionRef.current = trialSubscription;
            return;
          }
        }

        if (trialSubscription === "unknown") {
          updateUserAppSettings(state.user.id!, { trialSubscription: DEFAULT_TRIAL_SUBSCRIPTION });
          setTrialSubscription(DEFAULT_TRIAL_SUBSCRIPTION);
          const newConfig = getAppConfigFromSubscription(
            { type: DEFAULT_TRIAL_SUBSCRIPTION },
            userRole,
            internalAppType,
          );
          onChangeAppFunctionalityConfig(newConfig, DEFAULT_TRIAL_SUBSCRIPTION);
          return;
        }

        if (
          trialSubscription !== tempTrialSubscriptionRef.current ||
          userRole !== tempUserRoleRef.current ||
          trialConfigOrgIdRef.current !== state.userOrg.id
        ) {
          let trialSubName: SubscriptionTypes | "unknown" = DEFAULT_TRIAL_SUBSCRIPTION;
          // @ts-ignore
          if (trialSubscription && trialSubscription !== "unknown") {
            trialSubName = trialSubscription;
          }
          const newConfig = getAppConfigFromSubscription({ type: trialSubName }, userRole, internalAppType);
          // @ts-ignore
          onChangeAppFunctionalityConfig(newConfig, trialSubName);
          tempTrialSubscriptionRef.current = trialSubscription;
          tempUserRoleRef.current = userRole;
          trialConfigOrgIdRef.current = state.userOrg.id ?? null;
        }
      }
    }
  }, [state.user, state.userOrg, state.orgPrivateData, trialSubscription, internalAppType, userProfileLoadedFromServer]);

  // --- Fetch org and init sync subscription private data
  useEffect(() => {
    if (state.userOrg?.id && state.userOrg?.subscription) {
      if (!orgPrivateDataFetchedRef.current) {
        fetchOrgPrivateData(state.userOrg?.id);
        fetchProviderOrgToOrgSubscriptionTypes(state.userOrg?.id);
        fetchOrgToOrgProviderSubscriptions(state.userOrg?.id);
        fetchOrgToOrgConsumerSubscriptions(state.userOrg?.id);
        orgPrivateDataFetchedRef.current = true;
      }
      if (!subscriptionSyncedRef.current) {
        callCloudFunction("syncSubscription", { orgId: state.userOrg?.id });
        subscriptionSyncedRef.current = true;
      }
    }
    // Set subscrption information for user on SoMe accounts
    if (state.userOrg?.subscription?.type) {
      if (
        !["free", "trial"].includes(state.userOrg.subscription.type) &&
        !someConversionSentRef.current
      ) {
        const url = `${window.location.host}/${conversionSubscriptionUrl}`;
        trackLinkedInVirtualPageView(forslerPartnerId, url);
        trackFacebookEventWithUrl(forslerPixelId, "PageView", url);
        someConversionSentRef.current = true;
      }
    }
    // Fetch org data layers
    if (state.userOrg?.id && !orgExternalLayersFetchedRef.current) {
      fetchExternalOrgDataLayers(state.userOrg.id);
      orgExternalLayersFetchedRef.current = true;
    }
  }, [state.userOrg]);

  // Keep subscription-filtered layer catalogues in sync with the active trial selection.
  const externalLayerCountries = (
    state.countryCodes?.length ? state.countryCodes : [state.userOrg?.countryCode || ""]
  ) as TCountryCode[];
  const externalLayerSubscription = state.userOrg?.subscription?.type === "trial"
    ? (trialSubscription && trialSubscription !== "unknown" ? trialSubscription : DEFAULT_TRIAL_SUBSCRIPTION)
    : state.userOrg?.subscription?.type || "free";
  useSubscriptionDataLayers(
    state.userOrg?.id,
    externalLayerCountries,
    externalLayerSubscription,
    (countries, subscription, isCurrent) => {
      void fetchExternalWMSLayers("countryCode", countries, subscription, isCurrent);
      void fetchExternalRESTLayers("countryCode", countries, subscription, isCurrent);
    },
  );

  useEffect(() => {
    if (
      state.user &&
      state.user.id &&
      state.userOrg?.subscription &&
      state.userOrg?.subscription.type !== "trial" &&
      state.orgPrivateData
    ) {
      // Find user org role
      let userRole: "admin" | "not-admin" = "not-admin";
      if (state.orgPrivateData.roles && state.orgPrivateData.roles[state.user?.id]) {
        userRole = state.orgPrivateData.roles[state.user?.id].role;
      }
      const config = getAppConfigFromSubscription(state.userOrg.subscription, userRole, internalAppType);
      onChangeAppFunctionalityConfig(config, config.name);
      if (config.initialUserAppSettings && appSubConfigInitialUserSettingsState !== "initialized") {
        setFullState(config.initialUserAppSettings);
        setAppSubConfigInitialUserSettingsState("initialized");
      }
    }
  }, [state.userOrg, state.orgPrivateData, state.user?.id, internalAppType]);

  // --- Fetch org team and schemes --- //
  useEffect(() => {
    if (state.userOrg?.id && state.orgPrivateData?.roles && !orgAdditionDataFetchedRef.current) {
      // Fetch org data schemes
      fetchOrgSpeciesSchemes(state.userOrg.id);
      // Fetch team members
      fetchOrgTeam(state.userOrg.id);
      orgAdditionDataFetchedRef.current = true;
    }
  }, [state.userOrg, state.orgPrivateData]);

  // Fetch org Contacts
  const contactsKey = state.userOrg?.contacts
    ?.map(
      (c: { userCreated: boolean; id: string }) => `${c.userCreated ? "user" : "invite"}:${c.id}`,
    )
    .sort()
    .join("|");

  useEffect(() => {
    if (!state.userOrg?.contacts) return;
    fetchOrgContacts(state.userOrg.contacts);
    return () => {
      unsubscribeOrgContactListeners();
    };
  }, [contactsKey]);

  // Cache the basic buyer organisation projection as contacts load. This makes
  // both buyer requirements and delivery-note details available to stack
  // dialogs without a foreground request.
  useEffect(() => {
    if (!state.user) {
      buyerOrgFetchesRef.current.clear();
      return;
    }

    const buyerOrgsById = state.buyerOrgsById || {};
    const ownOrgId = state.userOrg?.id || undefined;
    if (ownOrgId && !Object.prototype.hasOwnProperty.call(buyerOrgsById, ownOrgId)) {
      dispatch({
        type: SET_BUYER_ORG_DATA,
        payload: {
          orgId: ownOrgId,
          orgData: state.userOrg,
        },
      });
    }

    const contactOrgIds = Object.values(state.orgContacts || {})
      .map((contact) => contact.orgId)
      .filter((orgId): orgId is string => Boolean(orgId));

    [...new Set(contactOrgIds)].forEach((orgId) => {
      const isCached = Object.prototype.hasOwnProperty.call(buyerOrgsById, orgId);
      if (isCached || buyerOrgFetchesRef.current.has(orgId)) return;

      buyerOrgFetchesRef.current.add(orgId);
      void getOrgData({ orgId })
        .then((result) => {
          const orgData = (result.data as { data?: UserOrg } | undefined)?.data;
          dispatch({
            type: SET_BUYER_ORG_DATA,
            payload: {
              orgId,
              orgData: orgData || {},
            },
          });
        })
        .catch((error) => {
          console.error("Unable to prefetch buyer organisation data", { orgId, error });
        })
        .finally(() => {
          buyerOrgFetchesRef.current.delete(orgId);
        });
    });
  }, [
    state.buyerOrgsById,
    state.orgContacts,
    state.user,
    state.userOrg?.id,
  ]);

  // --- Set deep links --- //
  useEffect(() => {
    if (window.location.search) {
      const urlParams = convertUrlQueryString(window.location.search);
      // If there is a distinct_id, identify the user with that distinct_id until the user signs in
      // And gets the proper id
      if (urlParams && urlParams.has("distinct_id")) {
        const distinct_id = urlParams.get("distinct_id");
        if (distinct_id) {
          urlParams.delete("distinct_id");
          mixpanel.identify(distinct_id);
          mpDistinctIdRef.current = distinct_id;
        }
      }
      // Remove parameters from the url
      const newUrl = window.location.pathname;
      if (!urlParams.has("audience")) window.history.replaceState({}, "", newUrl);
      setUrlParams(urlParams);
    }
  }, []);

  // --- Reducer dispatch functions --- //
  const setUrlParams = (params: any) => {
    dispatch({
      type: SET_URL_PARAMS,
      payload: {
        urlParams: params,
      },
    });
  };

  // --- Set background print job loading --- //
  const setBackgroundPrintJobLoading = (loading: boolean) => {
    dispatch({
      type: SET_BACKGROUND_PRINT_JOB_LOADING,
      payload: {
        backgroundPrintJobLoading: loading,
      },
    });
  };

  // --- Set children initialized --- //
  const setChildrenInitialized = () => {
    dispatch({
      type: CHILDREN_IS_INITIALIZED,
    });
  };

  // --- Set countryCodes --- //
  const setCountryCodes = (countryCodes: string[]) => {
    dispatch({
      type: SET_COUNTRY_CODES,
      payload: {
        countryCodes: countryCodes,
      },
    });
  };

  // --- Set selected species scheme --- //
  const setSelectedSpeciesSchemeId = (id: string | null) => {
    dispatch({
      type: SET_SELECTED_SPECIES_SCHEME_ID,
      payload: {
        selectedSpeciesSchemeId: id,
      },
    });
  };

  // --- Email functionality --- //
  const sendEmail = async (to: string, subject: string, text?: string) => {
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      await addDoc(collection(firestore, "mail"), {
        to: to,
        message: {
          subject: subject,
          text: text ? text : "",
        },
      });
      dispatch({ type: SET_LOADING, payload: { loading: false } });
    } catch (error) {
      console.error("Send email ERROR", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };
  // --- Sign in user --- //
  const firebaseAnonymousSignIn = () => signInAnonymously(auth);

  const firebaseEmailPasswordSignIn = (email: string, password: string) =>
    signInWithEmailAndPassword(auth, email, password);

  const unsubscribeAllFirestoreListeners = () => {
    firestoreEventListeners.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (err) {
        console.warn("Failed to unsubscribe Firestore listener", err);
      }
    });
    firestoreEventListeners = [];
    unsubscribeOrgContactListeners();
  };

  const unsubscribeOrgContactListeners = () => {
    orgContactsUnsubscribes.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (err) {
        console.warn("Failed to unsubscribe Firestore listener", err);
      }
    });
    orgContactsUnsubscribes = [];

    orgInvitesUnsubscribes.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (err) {
        console.warn("Failed to unsubscribe Firestore listener", err);
      }
    });
    orgInvitesUnsubscribes = [];
  };

  const resetAuthRefs = () => {
    subscriptionSyncedRef.current = false;
    orgPrivateDataFetchedRef.current = false;
    orgAdditionDataFetchedRef.current = false;
    someConversionSentRef.current = false;
    orgExternalLayersFetchedRef.current = false;
    initWithoutOrgRef.current = null;
  };

  // const firebaseLoginWithNative = async (email: string, password: string) => {
  //   const result = await FirebaseAuthentication.signInWithEmailAndPassword({ email, password });
  //   const credential = EmailAuthProvider.credential(email, password);
  //   const res = await signInWithCredential(auth, credential);
  //   return result as unknown as UserCredential;
  // };

  const firebaseGoogleSignIn = () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  };

  const firebaseTwitterSignIn = () => {
    const provider = new TwitterAuthProvider();
    return signInWithPopup(auth, provider);
  };

  const firebaseFacebookSignIn = () => {
    const provider = new FacebookAuthProvider();
    return signInWithPopup(auth, provider);
  };

  // --- Register user --- //
  const firebaseRegister = async (email: string, password: string) =>
    createUserWithEmailAndPassword(auth, email, password);

  // --- Logout user --- //
  const signalLogout = () => {
    dispatch({
      type: LOGOUT,
    });
  };
  // const logout = async () => {
  //   signalLogout();
  //   // await wait(10000);
  //   await signOut(auth);
  //   window.location.reload();
  //   return;
  // };
  const logout = async () => {
    const logoutPath = getLogoutPath(window.location.search);
    setLoadingUserInfo(true);
    unsubscribeAllFirestoreListeners();
    resetAuthRefs();
    try {
      await signOut(auth);
    } finally {
      dispatch({ type: LOGOUT });
      setLoadingUserInfo(false);
      // Reload audience links so the next session reads fresh URL parameters and profile config.
      if (logoutPath.startsWith("/anonymous-login?")) window.location.replace(logoutPath);
      else navigate(logoutPath, { replace: true });
    }
  };

  // --- Reset user password --- //
  const resetPassword = async (email: string): Promise<passwordReset.RequestResponse> => {
    const language = i18n === "da" ? "da" : "en";
    const brand = getInternalAppType({ pathOrHost: window.location.href });
    const result = await requestPasswordReset({ email, brand, language });
    return result.data;
  };

  // --- Update user profile --- //
  async function updateProfile(user: UpdateUserProfileProps, newUser?: boolean) {
    if (!newUser) assertDomainMutationAllowed();
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      const docRef = doc(firestore, `users/${user.id}`);
      // Create new user in users collection
      if (newUser) {
        // Get internalAppType
        const internalAppType = getInternalAppType({ pathOrHost: window.location.href });
        await setDoc(docRef, {
          ...dataObjectWithoutUndefinedFields(user),
          acceptedTerms: true,
          newUser: true,
          onboarded: false,
          creationDate: dayjs().format(),
          lastModifiedDate: dayjs().format(),
          internalAppType,
        });
        mixpanel.people.set({
          $accountRole: "Creator",
        });
      } else {
        const profile = {
          ...dataObjectWithoutUndefinedFields(user),
          lastModifiedDate: dayjs().format(),
          lastModifiedBy: {
            id: state.user?.id || "",
            name: `${state.user?.firstName || ""} ${state.user?.lastName || ""}`.trim(),
            email: state.user?.email || "",
          },
        };
        if (user.onboarded === true) {
          await completeOnboardingProfile(firestore, user.id, profile);
        } else {
          await updateDoc(docRef, profile);
        }
      }
      dispatch({ type: SET_LOADING, payload: { loading: false } });
    } catch (error) {
      console.error("updateProfile error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
      if (user.onboarded === true) throw error;
    }
  }

  const updateUserOnboardingObject = async (userId: string, onbObj: OnboardingObject) => {
    assertDomainMutationAllowed();
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      const docRef = doc(firestore, `users/${userId}/private_data/onboarding`);
      await setDoc(
        docRef,
        {
          ...dataObjectWithoutUndefinedFields(onbObj),
        },
        { merge: true },
      );
      dispatch({
        type: FETCH_USER_ONBOARDING_OBJECT_SUCCESS,
        payload: {
          userOnboardingObject: { ...state.userOnboardingObject, ...onbObj } as any,
        },
      });
      // Log event in mixpanel (onboarding step)
      mixpanel.people.set({
        onboardingStep: mapOnboardingStepToMixpanelUserOnbardingStep(onbObj),
      });
      if (onbObj.questionaireAnswers?.roleId) {
        mixpanel.people.set({
          $userRoles: onbObj.questionaireAnswers.roleId,
        });
      }
      dispatch({ type: SET_LOADING, payload: { loading: false } });
    } catch (error) {
      console.error("updateUserOnboarding error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
      throw error;
    }
  };

  const updateUserSettings = async (userId: string, data: UserSettingsObject) => {
    assertDomainMutationAllowed();
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      const docRef = doc(firestore, `users/${userId}/private_data/settings`);
      await setDoc(
        docRef,
        {
          ...dataObjectWithoutUndefinedFields(data),
        },
        { merge: true },
      );
      dispatch({ type: SET_LOADING, payload: { loading: false } });
    } catch (error) {
      console.error("updateUserSettings error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // --- Update organisation data --- //
  const updateOrganization = async (orgId: string | null, data: UserOrg, companyData?: any) => {
    assertDomainMutationAllowed();
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      let docId = null;
      if (!orgId) {
        const userId = state.user?.id;
        if (!userId || auth.currentUser?.uid !== userId) throw new Error("init/authenticated-user-required");
        if (!isOnline) throw new Error("init/online-user-required");
        const internalAppType = getInternalAppType({ pathOrHost: window.location.href });
        docId = await setupOnboardingOrganization(initUserAndOrg, userId, {
          ...dataObjectWithoutUndefinedFields(data),
          internalAppType,
        });
      } else {
        const docRef = doc(firestore, `orgs/${orgId}`);
        docId = orgId;
        await updateDoc(docRef, {
          ...dataObjectWithoutUndefinedFields(data),
          lastModifiedDate: dayjs().format(),
          lastModifiedBy: {
            id: state.user?.id || "",
            name: `${state.user?.firstName || ""} ${state.user?.lastName || ""}`.trim(),
            email: state.user?.email || "",
          },
        });
      }
      // If external company registration data, set it on cloud
      if (companyData) {
        const docRef = doc(firestore, `orgs/${docId}/private_data/companyData`);
        assertDomainMutationAllowed();
        await setDoc(
          docRef,
          {
            ...companyData,
          },
          { merge: true },
        );
      }
      dispatch({ type: SET_LOADING, payload: { loading: false } });
    } catch (error) {
      console.error("updateOrg error", { error });
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
      if (!orgId) throw error;
    }
  };

  const deleteOrgToOrgSubscription = async (id: string) => {
    assertDomainMutationAllowed();
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      const docRef = doc(firestore, `orgToOrgSubscriptions/${id}`);
      await deleteDoc(docRef);
      // Log event in mixpanel
      callCloudFunction("trackEvent", { event: mixpanelEvents.orgToOrgSubscriptionDeleted });
      dispatch({ type: SET_LOADING, payload: { loading: false } });
    } catch (error) {
      console.error("deleteOrgToOrgSubscription error", { error });
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // Branding functions
  const uploadBrandingAsset = async (
    brandingPackageId: string,
    orgId: string,
    assetKey: BrandingAssetKey,
    file: File,
  ) => {
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      const data = { orgId, brandingPackageId, assetKey, contentType: file.type };
      const upload = await callCloudFunction("getBrandingUploadUrl", { ...data, size: file.size });
      const uploadData = upload?.data as { uploadUrl?: unknown; objectPath?: unknown } | null | undefined;
      const { uploadUrl, objectPath } = uploadData ?? {};
      if (typeof uploadUrl !== "string" || !uploadUrl || typeof objectPath !== "string" || !objectPath) {
        throw new Error("No upload url or object path returned from server");
      }
      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!response.ok) throw new Error(`Upload failed (${response.status})`);
      const committed = await callCloudFunction("commitBrandingAsset", { ...data, objectPath });
      const committedData = committed?.data as { url?: unknown } | null | undefined;
      // The callable wrapper can return undefined for offline/session failures.
      if (typeof committedData?.url !== "string" || !committedData.url) {
        throw new Error("Branding asset save was not confirmed");
      }
      // Log event in mixpanel
      callCloudFunction("trackEvent", { event: mixpanelEvents.brandingAssetUploaded });
    } catch (error) {
      console.error("uploadBrandingAsset error", { error });
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
      throw error;
    } finally {
      dispatch({ type: SET_LOADING, payload: { loading: false } });
    }
  };

  const deleteBrandingAsset = async (
    brandingPackageId: string,
    orgId: string,
    assetKey: BrandingAssetKey,
  ) => {
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      const removed = await callCloudFunction("removeBrandingAsset", {
        orgId,
        brandingPackageId,
        assetKey,
      });
      const result = removed?.data;
      if (!result || typeof result !== "object" || (result as { status?: unknown }).status !== "success") {
        throw new Error("Branding asset removal was not confirmed");
      }
      // Log event in mixpanel
      callCloudFunction("trackEvent", { event: mixpanelEvents.brandingAssetDeleted });
    } catch (error) {
      console.error("deleteBrandingAsset error", { error });
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
      throw error;
    } finally {
      dispatch({ type: SET_LOADING, payload: { loading: false } });
    }
  };

  const updateBrandingPackage = async ({
    orgId,
    brandingPackageId,
    type,
    key,
    fieldKey,
    value,
  }: UpdateBrandingPackageType) => {
    // Input checking
    if (type === "section" && !fieldKey) {
      console.error(
        "updateBrandingPackage error: sectionKey and topKey are required for section type",
        { orgId, brandingPackageId, type, key, fieldKey, value },
      );
      return;
    }
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      if (type === "top") {
        await callCloudFunction("updateBrandingPackageFields", {
          orgId,
          brandingPackageId,
          updates: {
            [key]: value,
          },
        });
      } else if (type === "section") {
        await callCloudFunction("updateBrandingPackageFields", {
          orgId,
          brandingPackageId,
          updates: {
            [key]: {
              [fieldKey!]: value,
            },
          },
        });
      }
      // Log event in mixpanel
      callCloudFunction("trackEvent", { event: mixpanelEvents.brandingFieldsUpdated });
      dispatch({ type: SET_LOADING, payload: { loading: false } });
    } catch (error) {
      console.error("updateBrandingPackage error", { error });
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // --- Set AI assistant data --- //
  const setAIAssistantData = (
    type: "threadId" | "messages" | "openAiChatWindow",
    value: string | null | AiChatMessage[] | boolean,
  ) => {
    if (isDomainReadOnly()) return;
    if (type === "threadId") {
      dispatch({
        type: SET_CURRENT_AI_ASSISTANT_THREAD_ID,
        payload: { currentThreadId: value as string | null },
      });
      if (state.user?.id) {
        const docRef = doc(firestore, `aichat/conversations/users/${state.user.id}`);
        setDoc(docRef, { currentThreadId: value }, { merge: true }).catch(console.error);
      }
    } else if (type === "messages") {
      dispatch({
        type: SET_CURRENT_AI_ASSISTANT_MESSAGES,
        payload: { messages: value as AiChatMessage[] },
      });
    } else if (type === "openAiChatWindow") {
      dispatch({
        type: SET_OPEN_AI_CHAT_WINDOW,
        payload: { openAiChatWindow: value as boolean },
      });
    }
  };

  const fetchAIConversation = async (userId: string) => {
    try {
      const sessionDoc = await getDoc(doc(firestore, `aichat/conversations/users/${userId}`));
      if (!sessionDoc.exists()) return;
      const threadId = sessionDoc.data()?.currentThreadId as string | null | undefined;
      if (!threadId) return;

      const messagesSnap = await getDocs(
        query(
          collection(firestore, `aichat/conversations/users/${userId}/${threadId}`),
          orderBy("index", "asc"),
        ),
      );

      const messages: AiChatMessage[] = messagesSnap.docs.flatMap((d) => {
        const raw = d.data();
        const content = Array.isArray(raw.content)
          ? raw.content.map((p: any) => p.text ?? "").join("")
          : "";
        if (raw.role === "system") return [];
        if (raw.role === "user" && content.startsWith("[INTERNAL]")) return [];
        const displayContent = raw.role === "user"
          ? content.replace(/^\[Context:[^\]]*\]\n?/, "")
          : content;
        return [{
          id: d.id,
          userId,
          threadId,
          role: (raw.role === "model" ? "assistant" : "user") as "user" | "assistant",
          content: displayContent,
          createdAt: raw.savedAt ? new Date(raw.savedAt).getTime() : Date.now(),
          type: "normal" as const,
        }];
      });

      dispatch({ type: SET_CURRENT_AI_ASSISTANT_THREAD_ID, payload: { currentThreadId: threadId } });
      dispatch({ type: SET_CURRENT_AI_ASSISTANT_MESSAGES, payload: { messages } });
    } catch (error) {
      console.error("Fetch AI conversation error", error);
    }
  };

  const loadAudienceMapInformationRevision = useCallback(async (
    mapId: string,
    revisionId: string,
  ): Promise<AudienceMapInformation> => {
    const currentRef = doc(firestore, "maps", mapId, "audienceContent", "current");
    const [revisionSnapshot, assetSnapshots, chartSnapshots] = await Promise.all([
      getDoc(doc(currentRef, "revisions", revisionId)),
      getDocs(collection(currentRef, "assets")),
      getDocs(collection(currentRef, "revisions", revisionId, "charts")),
    ]);
    if (!revisionSnapshot.exists()) throw new Error("Audience content revision is missing");
    const revision = AudienceContentTypes.audienceContentRevision.parse(revisionSnapshot.data());
    const assets = Object.fromEntries(
      assetSnapshots.docs.map((snapshot) => {
        const asset = AudienceContentTypes.audienceContentAsset.parse(snapshot.data());
        return [asset.id, asset];
      }),
    );
    const charts = Object.fromEntries(
      chartSnapshots.docs.map((snapshot) => {
        const chart = AudienceContentTypes.audienceChart.parse(snapshot.data());
        return [chart.id, chart];
      }),
    );
    return { content: revision.content, assets, charts };
  }, []);

  const loadAudienceMapInformation = useCallback(async (
    mapId: string,
  ): Promise<AudienceMapInformation | null> => {
    const currentRef = doc(firestore, "maps", mapId, "audienceContent", "current");
    const currentSnapshot = await getDoc(currentRef);
    if (!currentSnapshot.exists()) return null;

    const current = AudienceContentTypes.audienceContentCurrent.parse(currentSnapshot.data());
    return loadAudienceMapInformationRevision(mapId, current.revisionId);
  }, [loadAudienceMapInformationRevision]);

  const subscribeAudienceMapInformation = useCallback((
    mapId: string,
    onInformation: (information: AudienceMapInformation | null) => void,
    onError: (error: Error) => void,
  ): Unsubscribe => {
    const currentRef = doc(firestore, "maps", mapId, "audienceContent", "current");
    let requestVersion = 0;

    const unsubscribe = onSnapshot(
      currentRef,
      async (currentSnapshot) => {
        const version = ++requestVersion;
        if (!currentSnapshot.exists()) {
          onInformation(null);
          return;
        }

        try {
          const current = AudienceContentTypes.audienceContentCurrent.parse(currentSnapshot.data());
          const information = await loadAudienceMapInformationRevision(mapId, current.revisionId);
          if (version === requestVersion) onInformation(information);
        } catch (error) {
          if (version === requestVersion) {
            onError(error instanceof Error ? error : new Error("Unable to load audience information"));
          }
        }
      },
      (error) => onError(error),
    );

    return () => {
      requestVersion += 1;
      unsubscribe();
    };
  }, [loadAudienceMapInformationRevision]);

  // --- Cloud Functions --- //
  const callCloudFunction = async (fcn: CloudFunctionNames, data: any) => {
    // Audience visitors may only issue non-mutating map support requests.
    if (!["trackEvent", "getNextAvailableId", "checkCustomIdAvailable"].includes(fcn)) {
      assertDomainMutationAllowed();
    }
    // Check for online status
    try {
      if (!isOnline || !auth.currentUser) {
        const error = !auth.currentUser
          ? new Error("no-auth-user")
          : new Error("cloud-function-offline");
        throw error;
      }
      switch (fcn) {
        // Init
        case "initUserAndOrg":
          return initUserAndOrg(data);
        // Tools
        case "getNextAvailableId":
          return getNextAvailableId(data);
        case "checkCustomIdAvailable":
          return checkCustomIdAvailable(data);
        case "releaseReservedId":
          return releaseReservedId(data);
        case "reserveNextId":
          return reserveNextId(data);
        case "reserveCustomId":
          return reserveCustomId(data);
        // Team
        case "createTeamMember":
          callCloudFunction("trackEvent", { event: mixpanelEvents.teamMemberCreated });
          return createTeamMember(data);
        case "readTeamMember":
          return readTeamMember(data);
        case "updateTeamMember":
          return updateTeamMember(data);
        case "deleteTeamMember":
          callCloudFunction("trackEvent", { event: mixpanelEvents.teamMemberDeleted });
          return deleteTeamMember(data);
        // Contacts
        case "createContact":
          callCloudFunction("trackEvent", { event: mixpanelEvents.contactCreated });
          return createContact(data);
        case "readContact":
          return readContact(data);
        case "updateContact":
          return updateContact(data);
        case "deleteContact":
          callCloudFunction("trackEvent", { event: mixpanelEvents.contactDeleted });
          return deleteContact(data);
        // Audience
        case "setAudienceState":
          return setAudienceState(data);
        case "createAudienceContentAssetUploadUrl":
          return createAudienceContentAssetUploadUrl(data);
        case "commitAudienceContentAsset":
          return commitAudienceContentAsset(data);
        case "saveAudienceContentRevision":
          return saveAudienceContentRevision(data);
        // Data
        case "deleteOrgAccount":
          return deleteOrgAccount(data);
        case "deleteUserAccount":
          return deleteUserAccount(data);
        case "getOrgData":
          return getOrgData(data);
        case "setDataStatus":
          return setDataStatus(data);
        case "createMetaData":
          return createMetaData(data);
        case "updateMapData":
          return updateMapData(data);
        case "orgToOrgSubscription":
          return orgToOrgSubscription(data);
        case "syncSubscription":
          return syncSubscription(data);
        case "updateSubscription":
          return updateSubscription(data);
        case "getCheckoutSession":
          return getCheckoutSession(data);
        case "reportLayerError":
          return reportLayerError(data);
        case "updateUsersBatch":
          return updateUsersBatch(data);
        // Branding
        case "ensureBrandingPackage":
          return ensureBrandingPackage(data);
        case "getBrandingUploadUrl":
          return getBrandingUploadUrl(data);
        case "commitBrandingAsset":
          return commitBrandingAsset(data);
        case "removeBrandingAsset":
          return removeBrandingAsset(data);
        case "updateBrandingPackageFields":
          return updateBrandingPackageFields(data);
        // External services
        case "getCompanyData":
          return getCompanyData(data);
        case "trackEvent":
          return trackEvent(data);

        default:
          throw new Error("cloud-function-unknown");
      }
    } catch (error) {
      console.error(`Cloud function ${fcn} error`, error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error,
        },
      });
    }
  };

  // Audience-content saving must propagate its callable failure to the editor.
  // The legacy generic dispatcher records errors and resolves undefined, which
  // prevents the editor from distinguishing a failed save from a missing result.
  const saveAudienceMapInformation = async (data: {
    mapId: string;
    content: unknown;
    charts: unknown[];
  }): Promise<void> => {
    assertDomainMutationAllowed();
    if (!isOnline) throw new Error("cloud-function-offline");
    if (!auth.currentUser) throw new Error("no-auth-user");
    await saveAudienceContentRevision(data);
  };

  const deleteAudienceMapInformation = async (mapId: string): Promise<void> => {
    assertDomainMutationAllowed();
    if (!isOnline) throw new Error("cloud-function-offline");
    if (!auth.currentUser) throw new Error("no-auth-user");
    await deleteAudienceContent({ mapId });
  };

  const audienceTemplateRequest = async <T,>(
    callable: (data: any) => Promise<T>,
    data: any,
  ): Promise<T> => {
    assertDomainMutationAllowed();
    if (!isOnline) throw new Error("cloud-function-offline");
    if (!auth.currentUser) throw new Error("no-auth-user");
    return callable(data);
  };

  const saveAudienceMapInformationTemplate = async (
    data: any,
  ): Promise<HttpsCallableResult<AudienceContentTypes.AudienceContentTemplateListItem>> => {
    const response = await audienceTemplateRequest(saveAudienceContentTemplateRevision, data);
    return {
      ...response,
      data: AudienceContentTypes.audienceContentTemplateListItem.parse(response.data),
    };
  };
  const listAudienceMapInformationTemplates = async () => {
    const response = await audienceTemplateRequest(listAudienceContentTemplates, {});
    if (!Array.isArray(response.data)) throw new Error("Invalid audience template list response");
    return response.data.map((template) =>
      AudienceContentTypes.audienceContentTemplateListItem.parse(template),
    );
  };
  const applyAudienceMapInformationTemplate = async (data: any) =>
    audienceTemplateRequest(applyAudienceContentTemplate, data);
  const deleteAudienceMapInformationTemplate = async (data: any) =>
    audienceTemplateRequest(deleteAudienceContentTemplate, data);

  // --- Fetch current software version --- //
  const fetchCurrentSoftwareVersion = async () => {
    try {
      const path = "data/version";
      const docRef = doc(firestore, path);
      const unsub = onSnapshot(docRef, (doc) => {
        if (doc.exists() && doc.data()) {
          dispatch({
            type: SET_CURRENT_SOFTWARE_VERSION,
            payload: {
              currentSoftwareVersion: doc.data().version ? doc.data().version : "",
            },
          });
        }
      });
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch software version error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // --- Fetch configuration data --- //
  const fetchSpeciesList = async (availability?: "simple" | "extended") => {
    try {
      const path = `data/stand/species`;
      // Set up query
      let q = null;
      if (availability && availability === "simple") {
        q = query(collection(firestore, path), where("availability", "==", "simple"));
      } else {
        q = query(collection(firestore, path));
      }
      const unsub = onSnapshot(
        q,
        (qs) => {
          let data = {} as any;
          qs.forEach((doc: any) => {
            data = { ...data, [doc.id]: doc.data() };
          });
          dispatch({
            type: FETCH_SPECIES_DATA_SUCCESS,
            payload: {
              species: data,
            },
          });
        },
        (error) => {
          console.info("ERROR TEST", error);
          console.trace();
        },
      );
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch species data error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  const fetchOrgSpeciesSchemes = async (orgId: string) => {
    try {
      const path = `data/stand/schemes`;
      // Set up query
      const q = query(collection(firestore, path), where("creatorOrgId", "==", orgId));
      // Run query
      const unsub = onSnapshot(q, (qs) => {
        qs.docChanges().forEach(async (change) => {
          switch (change.type) {
            case "added":
              dispatch({
                type: ADDED_SPECIES_SCHEME,
                payload: change.doc.data(),
              });
              break;
            case "modified":
              dispatch({
                type: MODIFIED_SPECIES_SCHEME,
                payload: change.doc.data(),
              });
              break;
            case "removed":
              dispatch({
                type: REMOVED_SPECIES_SCHEME,
                payload: change.doc.data(),
              });
              break;
          }
        });
      });
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch org species schemes failed", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  const fetchSpeciesScheme = async (id: string) => {
    try {
      if (state.speciesSchemes && state.speciesSchemes[id]) {
        // Already in state
        return;
      }
      const path = `data/stand/schemes/${id}`;
      const docRef = doc(firestore, path);
      const unsub = onSnapshot(docRef, (doc) => {
        if (doc.exists() && doc.data()) {
          dispatch({
            type: MODIFIED_SPECIES_SCHEME,
            payload: doc.data(),
          });
        }
      });
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch species scheme failed", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  const fetchActivityTypes = async (availability?: "simple" | "extended") => {
    try {
      const path = `data/activities/types`;
      // Set up query
      let q = null;
      if (availability && availability === "simple") {
        q = query(collection(firestore, path), where("availability", "==", "simple"));
      } else {
        q = query(collection(firestore, path));
      }
      const unsub = onSnapshot(
        q,
        (qs) => {
          let data = {} as any;
          qs.forEach((doc: any) => {
            data = { ...data, [doc.id]: doc.data() };
          });
          dispatch({
            type: FETCH_ACTIVITY_DATA_SUCCESS,
            payload: {
              activityTypes: data,
            },
          });
        },
        (error) => {
          console.info("ERROR TEST", error);
          console.trace();
        },
      );
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch activity types error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  const fetchSubscriptionAddOns = async () => {
    try {
      const path = `data/reepay/addOns`;
      const unsub = onSnapshot(
        collection(firestore, path),
        (qs) => {
          let data: any[] = [];
          qs.forEach((doc: any) => {
            data.push({ ...doc.data() });
          });
          dispatch({
            type: FETCH_SUBSCRIPTION_ADD_ONS_SUCCESS,
            payload: {
              subscriptionAddOns: data,
            },
          });
        },
        (error) => {
          console.info("ERROR TEST", error);
          console.trace();
        },
      );
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch subscription add-ons error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // --- Fetch user data --- //
  const fetchUserData = async (user: User) => {
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      const path = `users/${user.uid}`;
      let initWithoutOrg = true;
      const unsub = onSnapshot(
        doc(firestore, path),
        async (doc) => {
          // A missing cached document means Firestore has not resolved the
          // profile yet. It must never be treated as a new user.
          if (!doc.exists() && !user.isAnonymous) {
            if (doc.metadata.fromCache) return;
            const error = new Error("Authenticated user profile does not exist");
            console.error("Fetch user data error", error, { userId: user.uid });
            dispatch({ type: SET_ERROR, payload: { error } });
            return;
          }

          if (doc.exists() && !doc.metadata.fromCache && !doc.metadata.hasPendingWrites) {
            setUserProfileLoadedFromServer(true);
          }

          const userData: any = {
            id: user.uid,
            email: user.email!,
            authType: user.isAnonymous ? "anonymous" : "normal",
            ...(user.isAnonymous ? { firstName: translate("anonymous") } : {}),
            ...(doc.exists() ? doc.data() : {}),
          };
          // Store the profile before starting organization listeners, so their
          // snapshots can be matched to the current membership immediately.
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: { ...userData },
            },
          });
          if (doc.exists() && !isDomainReadOnly()) {
            // Fetch user org
            if (userData.orgId) {
              fetchUserOrg(userData.orgId);
              fetchOrgAppSettings(userData.orgId);
              initWithoutOrg = false;
              initWithoutOrgRef.current = "no";
            }
            if (userData.onboarded === false || !userData.orgId) await fetchUserOnboardingObject(userData.id);
            fetchAIConversation(userData.id);
          }
          // Set application config according to user profile
          if (userData.authType === "anonymous") {
            // @ts-ignore
            onChangeAppFunctionalityConfig(anonymousConfig as AppConfigOverride, "anonymous");
          }
          // Send user data to mixpanel
          mixpanel.people.set({
            $email: userData.email,
            $first_name: userData.firstName,
            $last_name: userData.lastName,
            $name: userData.firstName + " " + userData.lastName,
            $created: userData.creationDate,
          });
          if (initWithoutOrg) {
            initWithoutOrgRef.current = "yes";
            dispatch({
              type: IS_INITIALIZED,
            });
          }
        },
        (error) => {
          console.error("Fetch user data snapshot error", error);
          dispatch({ type: SET_ERROR, payload: { error } });
        },
      );
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch user data error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };
  // Fetch user onboarding object (used to preload onboarding)
  const fetchUserOnboardingObject = async (userId: string) => {
    try {
      const path = `users/${userId}/private_data/onboarding`;
      const onbDoc = await getDoc(doc(firestore, path));
      let onbObj = null;
      if (onbDoc.exists()) onbObj = onbDoc.data() as OnboardingObject;
      dispatch({
        type: FETCH_USER_ONBOARDING_OBJECT_SUCCESS,
        payload: {
          userOnboardingObject: onbObj,
        },
      });
    } catch (error) {
      console.error("Fetch user onboarding data error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // --- Fetch user org --- //
  const fetchUserOrg = async (orgId: string) => {
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      // Fetch org document
      const path = `orgs/${orgId}`;
      const unsub = onSnapshot(
        doc(firestore, path),
        async (doc) => {
          let orgData = null;
          if (doc.exists()) {
            orgData = { ...doc.data() } as UserOrg;
          }
          dispatch({
            type: FETCH_USER_ORG_SUCCESS,
            payload: {
              userOrg: orgData,
            },
          });
        },
        (error) => {
          console.info("ERROR TEST", error);
          console.trace();
        },
      );
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch user org data error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };
  // Fetch org private data
  const fetchOrgPrivateData = async (orgId: string) => {
    try {
      const path = `orgs/${orgId}/private_data/private`;
      const unsub = onSnapshot(
        doc(firestore, path),
        async (doc) => {
          const privateData = { ...doc?.data() };
          let authUserOrgRole: RoleTypes = "not-admin";
          // Set the authenticated users org role
          if (
            state.user &&
            state.user.id &&
            privateData &&
            privateData.roles &&
            privateData.roles[state.user.id]
          ) {
            authUserOrgRole = privateData.roles[state.user.id].role;
            // Log event in mixpanel
            mixpanel.people.set({
              $orgId: orgId,
              $orgRole: authUserOrgRole,
            });
          }
          // Disapatch to reducer
          // dataFetchingFinishedRef.current = true;
          dispatch({
            type: FETCH_ORG_PRIVATE_DATA_SUCCESS,
            payload: {
              orgPrivateData: privateData,
              orgId,
            },
          });
          dispatch({
            type: IS_INITIALIZED,
          });
        },
        (error) => {
          console.info("ERROR TEST", error);
          console.trace();
        },
      );
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch org private data error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // Fetch org to org subscription types offered by provider org
  // (see them as addOns like our reepay subscriptions)
  const fetchProviderOrgToOrgSubscriptionTypes = async (orgId: string) => {
    try {
      const path = "data/reepay/addOns";
      const q = query(collection(firestore, path), where("creatorOrgId", "==", orgId));
      const unsub = onSnapshot(q, (qs) => {
        let data: SubscriptionAddOn[] = [];
        qs.forEach((doc: any) => {
          data.push(doc.data() as SubscriptionAddOn);
        });
        dispatch({
          type: FETCH_PROVIDER_ORG_TO_ORG_SUBSCRIPTION_TYPES_SUCCESS,
          payload: {
            providerOrgToOrgSubscriptionTypes: data,
          },
        });
      });
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch provider org to org subscription types error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  const fetchOrgToOrgProviderSubscriptions = async (orgId: string) => {
    try {
      const path = `orgToOrgSubscriptions`;
      const q = query(collection(firestore, path), where("providerOrgId", "==", orgId));
      // const querySnapshot = await getDocs(q);
      const unsub = onSnapshot(q, (qs) => {
        let data: OrgToOrgSubscription[] = [];
        qs.forEach((doc: any) => {
          data.push(doc.data() as OrgToOrgSubscription);
        });
        dispatch({
          type: FETCH_ORG_TO_ORG_PROVIDER_SUBSCRIPTIONS_SUCCESS,
          payload: {
            orgToOrgProviderSubscriptions: data,
          },
        });
      });
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch org to org subscriptions error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  const fetchOrgToOrgConsumerSubscriptions = async (orgId: string) => {
    try {
      const path = `orgToOrgSubscriptions`;
      const q = query(collection(firestore, path), where("consumerOrgId", "==", orgId));
      const unsub = onSnapshot(q, (qs) => {
        let data: OrgToOrgSubscription[] = [];
        qs.forEach((doc: any) => {
          data.push(doc.data() as OrgToOrgSubscription);
        });
        dispatch({
          type: FETCH_ORG_TO_ORG_CONSUMER_SUBSCRIPTIONS_SUCCESS,
          payload: {
            orgToOrgConsumerSubscriptions: data,
          },
        });
      });
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch org to org subscriptions error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // Fetch external data layers (wms, wfs, ...)
  const fetchExternalWMSLayers = async (
    queryType: "orgId" | "estateId" | "countryCode",
    idStr: string | string[],
    subscription: SubscriptionTypes,
    isCurrent: () => boolean = () => true,
  ) => {
    try {
      // TODO: HACK: Account for custom subscriptions by setting sub to advanced when
      // ever the subscription is not a default subscription. We will later remove layers through the
      // App config files, where there is a remove layers array.
      let sub = subscription;
      if (!FirestoreTypes.subscriptionType.safeParse(sub).success) {
        sub = "advanced";
      }
      // Setup query
      const path = `data/externalDataLayers/wmsLayers/`;
      let c1 = null;
      const c2 = where("subscriptionAvailability", "array-contains", sub);
      const c3 = where("status", "==", "enabled");

      if (queryType === "countryCode") {
        c1 = where("countryCode", "in", getWMSLayerCountries(idStr));
      } else if (queryType === "orgId") {
        c1 = where("orgId", "==", idStr);
      } else if (queryType === "estateId") {
        c1 = where("estateId", "==", idStr);
      }
      if (!c1) return;
      const q = query(collection(firestore, path), c1, c2, c3);
      const querySnapshot = await getDocs(q);
      if (!isCurrent()) return;
      let data: any = {};
      querySnapshot.forEach((doc) => {
        data = { ...data, [doc.id]: doc.data() };
      });

      dispatch({
        type: FETCH_EXTERNAL_WMS_LAYERS_SUCCESS,
        payload: {
          externalWMSLayers: data,
        },
      });
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Fetch external WMS layers error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // Fetch REST data layers
  const fetchExternalRESTLayers = async (
    queryType: "orgId" | "estateId" | "countryCode",
    idStr: string | string[],
    subscription: SubscriptionTypes,
    isCurrent: () => boolean = () => true,
  ) => {
    try {
      // TODO: HACK: Account for custom subscriptions by setting sub to advanced when
      // ever the subscription is not a default subscription. We will later remove layers through the
      // App config files, where there is a remove layers array.
      let sub = subscription;
      if (!FirestoreTypes.subscriptionType.safeParse(sub).success) {
        sub = "advanced";
      }

      // Setup query
      const path = `data/externalDataLayers/restLayers/`;
      let c1 = null;
      const c2 = where("subscriptionAvailability", "array-contains", sub);
      const c3 = where("status", "==", "enabled");

      if (queryType === "countryCode") {
        if (Array.isArray(idStr)) {
          c1 = where("countryCode", "in", idStr);
        } else {
          c1 = where("countryCode", "==", idStr);
        }
      } else if (queryType === "orgId") {
        c1 = where("orgId", "==", idStr);
      } else if (queryType === "estateId") {
        c1 = where("estateId", "==", idStr);
      }

      if (!c1) return;
      const q = query(collection(firestore, path), c1, c2, c3);
      const querySnapshot = await getDocs(q);
      if (!isCurrent()) return;
      let data: any = {};
      querySnapshot.forEach((doc) => {
        data = { ...data, [doc.id]: doc.data() };
      });

      dispatch({
        type: FETCH_EXTERNAL_REST_LAYERS_SUCCESS,
        payload: {
          externalRESTLayers: data,
        },
      });
    } catch (error) {
      if (!isCurrent()) return;
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // Fetch org specific data layers (wms, wfs, esri, ...)
  const fetchExternalOrgDataLayers = async (orgId: string, subscription?: SubscriptionTypes) => {
    try {
      // Setup query
      const path = `data/externalDataLayers/esriMapServerLayers/`;
      const c1 = where("orgId", "==", orgId);
      const c2 = where("status", "==", "enabled");
      const c3 = where(
        "subscriptionAvailability",
        "array-contains",
        subscription ? subscription : "pro",
      );
      const q = query(collection(firestore, path), c1, c2, c3);
      // Execute query
      const querySnapshot = await getDocs(q);
      let data: any = {};
      querySnapshot.forEach((doc) => {
        data = { ...data, [doc.id]: doc.data() };
      });
      dispatch({
        type: FETCH_EXTERNAL_ORG_DATA_LAYERS_SUCCESS,
        payload: {
          externalOrgDataLayers: data,
        },
      });
    } catch (error) {
      console.error("Fetch external org data layers error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // --- Fetch org team --- //
  const fetchOrgTeam = async (orgId: string) => {
    try {
      const q = query(collection(firestore, "users"), where("orgId", "==", orgId));
      const unsub = onSnapshot(
        q,
        async (qs) => {
          qs.docChanges().forEach((change: any) => {
            switch (change.type) {
              case "added":
                dispatch({
                  type: ADDED_ORG_TEAM_MEMBER_SUCCESS,
                  payload: { memberData: { ...change.doc.data() } },
                });
                break;
              case "modified":
                dispatch({
                  type: MODIFIED_ORG_TEAM_MEMBER_SUCCESS,
                  payload: { memberData: { ...change.doc.data() } },
                });
                break;
              case "removed":
                dispatch({
                  type: REMOVED_ORG_TEAM_MEMBER_SUCCESS,
                  payload: { memberData: { ...change.doc.data() } },
                });
                break;
              default:
            }
          });
        },
        (error) => {
          console.info("ERROR TEST", error);
          console.trace();
        },
      );
      firestoreEventListeners.push(unsub);
    } catch (error) {
      console.error("Fetch user org team data error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // --- Delete team member from reducer state --- //
  const deleteTeamMemberFromState = (id: string) => {
    dispatch({ type: DELETE_TEAM_MEMBER_FROM_STATE, payload: { removeUserId: id } });
  };

  // --- Fetch org contacts --- //
  const fetchOrgContacts = async (contactList: { id: string; userCreated: boolean }[]) => {
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      unsubscribeOrgContactListeners();
      // Fetch contacts data
      const inviteIds: string[] = [];
      const contactIds: string[] = [];
      contactList.forEach((el) => {
        if (el.userCreated) contactIds.push(el.id);
        else inviteIds.push(el.id);
      });
      // Query for contacts where user has been created
      if (contactIds.length > 0) {
        const contactChuncks = chunk(contactIds, 10);
        contactChuncks.forEach((chunkedIds) => {
          const uQuery = query(collection(firestore, "users"), where("id", "in", chunkedIds));
          const unsub = onSnapshot(
            uQuery,
            async (qs) => {
              qs.docChanges().forEach((change: any) => {
                switch (change.type) {
                  case "added":
                    dispatch({
                      type: ADDED_ORG_CONTACT_SUCCESS,
                      payload: { contactData: { ...change.doc.data(), status: "user-created" } },
                    });
                    break;
                  case "modified":
                    dispatch({
                      type: MODIFIED_ORG_CONTACT_SUCCESS,
                      payload: { contactData: { ...change.doc.data(), status: "user-created" } },
                    });
                    break;
                  case "removed":
                    dispatch({
                      type: REMOVED_ORG_CONTACT_SUCCESS,
                      payload: { contactData: { ...change.doc.data(), status: "user-created" } },
                    });
                    break;
                  default:
                }
              });
            },
            (error) => {
              console.info("ERROR TEST", error);
            },
          );
          orgContactsUnsubscribes.push(unsub);
        });
      }
      if (inviteIds.length > 0) {
        const inviteChunks = chunk(inviteIds, 10);
        inviteChunks.forEach((chunkedIds) => {
          const uQuery = query(collection(firestore, "invitations"), where("id", "in", chunkedIds));
          const unsub = onSnapshot(
            uQuery,
            async (qs) => {
              qs.docChanges().forEach((change: any) => {
                switch (change.type) {
                  case "added":
                    dispatch({
                      type: ADDED_ORG_CONTACT_SUCCESS,
                      payload: { contactData: { ...change.doc.data(), status: "invite-pending" } },
                    });
                    break;
                  case "modified":
                    dispatch({
                      type: MODIFIED_ORG_CONTACT_SUCCESS,
                      payload: { contactData: { ...change.doc.data(), status: "invite-pending" } },
                    });
                    break;
                  case "removed":
                    dispatch({
                      type: REMOVED_ORG_CONTACT_SUCCESS,
                      payload: { contactData: { ...change.doc.data(), status: "invite-pending" } },
                    });
                    break;
                  default:
                }
              });
            },
            (error) => {
              console.info("ERROR TEST", error);
            },
          );
          orgInvitesUnsubscribes.push(unsub);
        });
      }
      if (contactIds.length === 0 && inviteIds.length === 0) {
        dispatch({
          type: ORG_CONTACTS_EMPTY,
        });
      }
      dispatch({ type: SET_LOADING, payload: { loading: false } });
    } catch (error) {
      console.error("Fetch user org data error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  const deleteContactFromState = (id: string) => {
    dispatch({ type: DELETE_CONTACT_FROM_STATE, payload: { removeUserId: id } });
  };

  // --- Error logging --- //
  const logError = async (error: Error, errorInfo: ErrorInfo) => {
    try {
      // Log error to firebase
      const docRef = doc(collection(firestore, "appErrorLog"));
      await setDoc(docRef, {
        id: docRef.id,
        page: location.pathname,
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack,
        errorInfo: errorInfo.componentStack,
        user: state.user,
        timeStamp: dayjs().format(),
      });
    } catch (error) {
      console.error("Error logging error", error);
      dispatch({
        type: SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  };

  // --- Check for data to be loaded --- //
  if (
    (state.isInitialized !== undefined && !state.isInitialized) ||
    (state.isLoggedIn && loadingUserInfo)
  ) {
    return <Loader />;
  }

  return (
    <FirebaseContext.Provider
      value={{
        ...state,
        userProfileLoadedFromServer,
        setChildrenInitialized,
        setCountryCodes,
        sendEmail,
        setUrlParams,
        logError,
        firebaseRegister,
        firebaseAnonymousSignIn,
        firebaseEmailPasswordSignIn,
        // firebaseLoginWithNative,
        login: () => {},
        firebaseGoogleSignIn,
        firebaseTwitterSignIn,
        firebaseFacebookSignIn,
        logout,
        resetPassword,
        updateProfile,
        updateOrganization,
        deleteOrgToOrgSubscription,
        uploadBrandingAsset,
        deleteBrandingAsset,
        updateBrandingPackage,
        updateUserOnboardingObject,
        updateUserSettings,
        loadAudienceMapInformation,
        subscribeAudienceMapInformation,
        saveAudienceMapInformation,
        deleteAudienceMapInformation,
        saveAudienceMapInformationTemplate,
        listAudienceMapInformationTemplates,
        applyAudienceMapInformationTemplate,
        deleteAudienceMapInformationTemplate,
        callCloudFunction,
        deleteTeamMemberFromState,
        deleteContactFromState,
        fetchSpeciesScheme,
        setSelectedSpeciesSchemeId,
        setBackgroundPrintJobLoading,
        setAIAssistantData,
      }}
    >
      <ErrorBoundary logErrorFunction={logError} translate={translate}>
        {children}
      </ErrorBoundary>
    </FirebaseContext.Provider>
  );
};

export default FirebaseContext;
